import { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const fmt  = n => "$" + Math.round(n ?? 0).toLocaleString("es-CL");
const pct  = n => (n ?? 0).toFixed(1) + "%";

const ESTATUS_COLOR = {
  "ENTREGADO":              "#1D9E75",
  "CONFIRMADO":             "#534AB7",
  "EN TRANSITO":            "#4F81BD",
  "PENDIENTE CONFIRMACION": "#EF9F27",
  "CANCELADO":              "#888",
  "DEVOLUCION":             "#E24B4A",
  "RECHAZADO":              "#E24B4A",
};
const statusColor = s => {
  if (!s) return "#aaa";
  const u = s.toUpperCase();
  for (const [k, v] of Object.entries(ESTATUS_COLOR)) {
    if (u.includes(k)) return v;
  }
  return "#888";
};
const statusShort = s => {
  if (!s) return "—";
  const u = s.toUpperCase();
  if (u.includes("ENTREGADO"))   return "Entregado";
  if (u.includes("TRANSITO"))    return "En tránsito";
  if (u.includes("CONFIRMADO"))  return "Confirmado";
  if (u.includes("PENDIENTE"))   return "Pendiente";
  if (u.includes("CANCELADO"))   return "Cancelado";
  if (u.includes("DEVOLUCION") || u.includes("RECHAZADO")) return "Fallido";
  return s;
};

function Badge({ texto, color }) {
  return (
    <span style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 500,
      background: color + "18", color, border: `1px solid ${color}30`,
      whiteSpace: "nowrap",
    }}>{texto}</span>
  );
}

function SeccionTitulo({ numero, titulo, color = "#111" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "1rem" }}>
      <div style={{
        width: 26, height: 26, borderRadius: "50%", background: color,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, fontWeight: 700, color: "#fff", flexShrink: 0,
      }}>{numero}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>{titulo}</div>
    </div>
  );
}

function KPICard({ label, value, color = "#111", sub, big = false }) {
  return (
    <div style={{ background: "#fafaf9", borderRadius: 10, padding: "12px 14px", borderBottom: `3px solid ${color}` }}>
      <div style={{ fontSize: 10, color: "#aaa", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: big ? 28 : 22, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "#bbb", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function TablaOrden({ columnas, filas, vacia = "Sin registros para este día." }) {
  if (!filas || filas.length === 0) {
    return <div style={{ padding: "1.5rem 0", color: "#aaa", fontSize: 12, textAlign: "center" }}>{vacia}</div>;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {columnas.map(c => (
              <th key={c.key} style={{
                textAlign: "left", padding: "5px 8px 8px", fontSize: 10,
                color: "#aaa", fontWeight: 600, textTransform: "uppercase",
                letterSpacing: "0.05em", borderBottom: "0.5px solid #eee",
                whiteSpace: "nowrap",
              }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((fila, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : "#fafaf9" }}>
              {columnas.map(c => (
                <td key={c.key} style={{
                  padding: "9px 8px", borderBottom: "0.5px solid #f5f5f4",
                  color: c.color ? c.color(fila) : "#444",
                  fontWeight: c.bold ? 600 : 400,
                  whiteSpace: c.nowrap ? "nowrap" : "normal",
                  maxWidth: c.maxW || undefined,
                  overflow: c.maxW ? "hidden" : undefined,
                  textOverflow: c.maxW ? "ellipsis" : undefined,
                }}>
                  {c.render ? c.render(fila) : fila[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AnalisisVentaLogistica() {
  const [data, setData]     = useState(null);
  const [error, setError]   = useState(null);
  const [loading, setLoading] = useState(true);

  // Tabs dentro de las secciones con tablas
  const [tabLog, setTabLog]   = useState("todos");
  const [tabFin, setTabFin]   = useState("ingresos");

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/analisis-venta-logistica`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then(r => { if (!r.ok) throw new Error("Sin datos disponibles"); return r.json(); })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "4rem", color: "#888", gap: 10 }}>
      <div style={{ width: 20, height: 20, border: "2px solid #eee", borderTop: "2px solid #1D9E75", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      Cargando análisis...
    </div>
  );

  if (error || !data) return (
    <div style={{ padding: "2rem", color: "#888", fontSize: 13, textAlign: "center" }}>
      {error || "No hay datos disponibles. Carga los archivos primero."}
    </div>
  );

  const { fecha, ventas, logistica, finanzas } = data;

  // Filtro logística
  const listaLogFiltrada = tabLog === "todos"
    ? logistica.lista
    : tabLog === "entregados"
      ? logistica.lista.filter(r => r.estatus.toUpperCase().includes("ENTREGADO"))
      : tabLog === "novedades"
        ? logistica.lista.filter(r => r.tiene_novedad)
        : logistica.lista.filter(r => r.estatus.toUpperCase().includes("DEVOLUCION") || r.estatus.toUpperCase().includes("RECHAZADO"));

  return (
    <div style={{ fontFamily: "system-ui, sans-serif" }}>

      {/* Header */}
      <div style={{ background: "#111", borderRadius: 14, padding: "1.25rem 1.5rem", marginBottom: "1.25rem", color: "#fff", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>
            Análisis Venta y Logística Diaria
          </div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>{fecha}</div>
        </div>
        <div style={{ display: "flex", gap: 20 }}>
          {[
            { label: "Pedidos del día",   value: ventas.total,             color: "#fff" },
            { label: "Procesados hoy",    value: logistica.procesados_hoy, color: "#4F81BD" },
            { label: "Entregados hoy",    value: logistica.entregados,     color: "#1D9E75" },
            { label: "Fallidos hoy",      value: logistica.fallidos,       color: "#E24B4A" },
          ].map(k => (
            <div key={k.label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 10, color: "#666" }}>{k.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CATEGORÍA 1: VENTAS ────────────────────────────────────────────── */}
      <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1.25rem", marginBottom: "1rem" }}>
        <SeccionTitulo numero="1" titulo="Venta" color="#534AB7" />

        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8, marginBottom: "1.25rem" }}>
          <KPICard label="Pedidos realizados"   value={ventas.total}         color="#111"     big />
          <KPICard label="Confirmados"          value={ventas.confirmados}   color="#534AB7" />
          <KPICard label="Por confirmar"        value={ventas.por_confirmar} color="#EF9F27" />
          <KPICard label="En curso"             value={ventas.en_curso}      color="#4F81BD" />
          <KPICard label="Tasa confirmación"    value={pct(ventas.tasa_confirmacion)} color={ventas.tasa_confirmacion >= 60 ? "#1D9E75" : ventas.tasa_confirmacion >= 40 ? "#EF9F27" : "#E24B4A"} sub="Confirmados / Total" />
          <KPICard label="Tasa entrega"         value={pct(ventas.tasa_entrega)}      color={ventas.tasa_entrega >= 70 ? "#1D9E75" : ventas.tasa_entrega >= 50 ? "#EF9F27" : "#E24B4A"}      sub="Entregados / Confirmados" />
          <KPICard label="Margen potencial"     value={fmt(ventas.margen_potencial)}  color="#1D9E75" sub="Según tasas históricas" />
        </div>

        {/* Tabla de pedidos del día */}
        <div style={{ fontSize: 11, color: "#aaa", marginBottom: "0.6rem" }}>
          Detalle de pedidos creados el {fecha}
        </div>
        <TablaOrden
          columnas={[
            { key: "orden_id",      label: "Orden ID",      bold: true, nowrap: true },
            { key: "cliente",       label: "Cliente",       maxW: 160 },
            { key: "producto",      label: "Producto",      maxW: 200 },
            { key: "transportadora",label: "Transportadora",nowrap: true },
            { key: "estatus",       label: "Estado",        render: r => <Badge texto={statusShort(r.estatus)} color={statusColor(r.estatus)} /> },
            { key: "valor",         label: "Valor",         bold: true, nowrap: true, render: r => fmt(r.valor) },
          ]}
          filas={ventas.lista}
          vacia={`No se registraron pedidos nuevos el ${fecha}.`}
        />
      </div>

      {/* ── CATEGORÍA 2: LOGÍSTICA ─────────────────────────────────────────── */}
      <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1.25rem", marginBottom: "1rem" }}>
        <SeccionTitulo numero="2" titulo="Logística" color="#4F81BD" />

        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: "1.25rem" }}>
          <KPICard label="En tránsito (total)"  value={logistica.en_transito_total} color="#4F81BD"  sub="Pendientes de entrega" />
          <KPICard label="Procesados hoy"       value={logistica.procesados_hoy}   color="#111"    big />
          <KPICard label="Entregados"           value={logistica.entregados}       color="#1D9E75" sub="Pagos acreditados" />
          <KPICard label="Con novedad"          value={logistica.con_novedad}      color="#EF9F27" sub="Incidencias registradas" />
          <KPICard label="Fallidos"             value={logistica.fallidos}         color="#E24B4A" sub="Devolución / Rechazo" />
        </div>

        {/* Filtros */}
        <div style={{ display: "flex", gap: 6, marginBottom: "0.85rem" }}>
          {[
            { key: "todos",      label: `Todos (${logistica.lista.length})` },
            { key: "entregados", label: `Entregados (${logistica.entregados})` },
            { key: "novedades",  label: `Con novedad (${logistica.con_novedad})` },
            { key: "fallidos",   label: `Fallidos (${logistica.fallidos})` },
          ].map(f => (
            <button key={f.key} onClick={() => setTabLog(f.key)} style={{
              fontSize: 11, padding: "4px 12px", borderRadius: 20, cursor: "pointer",
              border: tabLog === f.key ? "none" : "0.5px solid #ddd",
              background: tabLog === f.key ? "#111" : "transparent",
              color: tabLog === f.key ? "#fff" : "#666",
            }}>{f.label}</button>
          ))}
        </div>

        <TablaOrden
          columnas={[
            { key: "orden_id",       label: "Orden ID",       bold: true, nowrap: true },
            { key: "fecha_venta",    label: "Fecha venta",    nowrap: true, color: () => "#888" },
            { key: "cliente",        label: "Cliente",        maxW: 150 },
            { key: "producto",       label: "Producto",       maxW: 180 },
            { key: "transportadora", label: "Transportadora", nowrap: true },
            { key: "estatus",        label: "Estado",         render: r => <Badge texto={statusShort(r.estatus)} color={statusColor(r.estatus)} /> },
            { key: "novedad",        label: "Novedad",        maxW: 180, color: r => r.tiene_novedad ? "#EF9F27" : "#ddd",
              render: r => r.tiene_novedad ? <span style={{ color: "#EF9F27", fontSize: 11 }}>{r.novedad}</span> : <span style={{ color: "#ddd" }}>—</span> },
            { key: "valor",          label: "Valor",          bold: true, nowrap: true, render: r => fmt(r.valor) },
          ]}
          filas={listaLogFiltrada}
          vacia="Sin movimientos logísticos registrados para este día."
        />
      </div>

      {/* ── CATEGORÍA 3: INGRESOS Y SALIDAS ───────────────────────────────── */}
      <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1.25rem" }}>
        <SeccionTitulo numero="3" titulo="Ingresos y Salidas" color="#1D9E75" />

        {/* Totales */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: "1.25rem" }}>
          <div style={{ background: "#f4fbf8", borderRadius: 10, padding: "14px 18px", borderLeft: "4px solid #1D9E75" }}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Total ingresos del día</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#1D9E75" }}>{fmt(finanzas.ingresos_total)}</div>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>{finanzas.ingresos.length} transacciones</div>
          </div>
          <div style={{ background: "#fef7f0", borderRadius: 10, padding: "14px 18px", borderLeft: "4px solid #E24B4A" }}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Total salidas del día</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#E24B4A" }}>{fmt(finanzas.salidas_total)}</div>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>{finanzas.salidas.length} transacciones</div>
          </div>
        </div>

        {/* Tabs ingresos / salidas */}
        <div style={{ display: "flex", gap: 6, marginBottom: "0.85rem" }}>
          {[
            { key: "ingresos", label: `Ingresos (${finanzas.ingresos.length})`, color: "#1D9E75" },
            { key: "salidas",  label: `Salidas (${finanzas.salidas.length})`,   color: "#E24B4A" },
          ].map(f => (
            <button key={f.key} onClick={() => setTabFin(f.key)} style={{
              fontSize: 11, padding: "4px 12px", borderRadius: 20, cursor: "pointer",
              border: tabFin === f.key ? "none" : "0.5px solid #ddd",
              background: tabFin === f.key ? f.color : "transparent",
              color: tabFin === f.key ? "#fff" : "#666",
            }}>{f.label}</button>
          ))}
        </div>

        <TablaOrden
          columnas={[
            { key: "orden_id",    label: "Orden ID",    bold: true, nowrap: true },
            { key: "guia",        label: "Nº Guía",     nowrap: true, color: () => "#888" },
            { key: "descripcion", label: "Descripción", maxW: 320 },
            {
              key: "monto", label: "Monto", bold: true, nowrap: true,
              color: () => tabFin === "ingresos" ? "#1D9E75" : "#E24B4A",
              render: r => fmt(r.monto),
            },
          ]}
          filas={tabFin === "ingresos" ? finanzas.ingresos : finanzas.salidas}
          vacia={`Sin ${tabFin} registrados para el ${fecha}.`}
        />

        {/* Balance neto */}
        {(finanzas.ingresos_total > 0 || finanzas.salidas_total > 0) && (() => {
          const neto = finanzas.ingresos_total - finanzas.salidas_total;
          return (
            <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end" }}>
              <div style={{ background: neto >= 0 ? "#f4fbf8" : "#fef0f0", borderRadius: 9, padding: "10px 18px", display: "flex", gap: 16, alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "#888" }}>Balance neto del día:</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: neto >= 0 ? "#1D9E75" : "#E24B4A" }}>{fmt(neto)}</span>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
